import { useEffect, useState } from "react";
import { Building2, Gift, Users2, CalendarHeart, Send, Check } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/lib/Seo";
import { api, apiError, euros, mediaUrl } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import Reveal from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const USE_CASES = [
  { icon: Gift, title: "Cadeaux clients", text: "Un cadeau local, premium et fédérateur qui marque les esprits." },
  { icon: Users2, title: "Cadeaux collaborateurs", text: "Fêtez les réussites de vos équipes avec un morceau de Nice." },
  { icon: CalendarHeart, title: "Événements & séminaires", text: "Animez vos soirées d'entreprise autour d'un tournoi du 12e Homme." },
];

export default function Entreprises() {
  const { content } = useSite();
  const [sending, setSending] = useState(false);
  const [offers, setOffers] = useState([]);
  const [form, setForm] = useState({ company: "", contact_name: "", email: "", phone: "", quantity: 10, message: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    api.get("/public/corporate-offers").then((r) => setOffers(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/public/corporate", { ...form, quantity: Number(form.quantity) || 10 });
      toast.success("Demande envoyée !", { description: "Nous revenons vers vous sous 48h avec un devis." });
      setForm({ company: "", contact_name: "", email: "", phone: "", quantity: 10, message: "" });
    } catch (err) {
      toast.error(apiError(err, "Envoi impossible, réessayez."));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Seo title="Offre Entreprises" path="/entreprises"
        description="Douzième Homme — Le Jeu pour les entreprises : cadeaux clients, collaborateurs et événements. Tarif dégressif dès 10 boîtes sur la Côte d'Azur." />

      <section className="py-14 sm:py-20 lg:py-28" data-testid="b2b-hero">
        <div className="container-x max-w-4xl">
          <Reveal>
            <p className="eyebrow mb-4">{content.corporate_eyebrow}</p>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight uppercase leading-[1.05] text-onyx">
              {content.corporate_title}
            </h1>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-muted-foreground max-w-2xl">
              {content.corporate_text} Facturation entreprise et devis personnalisé sous 48h.
            </p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-5" data-testid="b2b-usecases">
            {USE_CASES.map(({ icon: Icon, title, text }, i) => (
              <Reveal key={title} delay={i * 0.08}>
                <div className="h-full bg-white border border-line p-7">
                  <Icon className="w-6 h-6 text-rouge mb-4" />
                  <h2 className="font-display text-lg font-bold text-onyx mb-2">{title}</h2>
                  <p className="text-sm text-muted-foreground leading-relaxed">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {offers.length > 0 && (
        <section className="py-14 sm:py-20 bg-onyx text-cream" data-testid="b2b-offers-section">
          <div className="container-x">
            <Reveal className="mb-10">
              <p className="eyebrow mb-4 !text-rouge-light">Tarifs dégressifs</p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight uppercase leading-[1.1]">
                Trois offres, un seul jeu.
              </h2>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="b2b-offers-grid">
              {offers.map((o, i) => (
                <Reveal key={o.id} delay={i * 0.08}>
                  <div className="h-full border border-cream/15 p-8 flex flex-col hover:border-rouge-light/60 transition-colors" data-testid={`b2b-offer-${i}`}>
                    {o.image && <img src={mediaUrl(o.image)} alt={o.name} className="h-16 w-auto object-contain mb-5 self-start" loading="lazy" />}
                    <h3 className="font-display text-xl font-bold uppercase tracking-tight">{o.name}</h3>
                    <p className="font-mono text-xs text-rouge-light font-bold mt-1 uppercase tracking-widest">{o.range_label}</p>
                    <p className="mt-5 font-display text-3xl font-extrabold">
                      {o.price_note && <span className="text-sm font-bold text-cream/60 align-middle mr-1.5">{o.price_note}</span>}
                      {euros(o.unit_price)} <span className="text-sm font-sans font-semibold text-cream/55">TTC / unité</span>
                    </p>
                    {o.description && <p className="mt-4 text-sm text-cream/65 leading-relaxed flex-1">{o.description}</p>}
                    {o.contents && (
                      <ul className="mt-4 space-y-2 text-sm text-cream/70">
                        {o.contents.split("\n").filter(Boolean).map((c) => (
                          <li key={c} className="flex items-start gap-2"><Check className="w-4 h-4 text-rouge-light shrink-0 mt-0.5" /> {c}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-14 sm:py-20 bg-white border-y border-line" data-testid="b2b-form-section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 bg-rouge flex items-center justify-center"><Building2 className="w-5 h-5 text-white" /></div>
              <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-onyx">Demander un devis</h2>
            </div>
            <p className="text-base text-muted-foreground leading-relaxed">
              Dès 10 boîtes, bénéficiez d'un tarif dégressif. Décrivez votre besoin : nous vous répondons
              avec une proposition adaptée à votre budget et vos délais.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-onyx font-medium">
              <li>· Tarif dégressif dès 10 boîtes</li>
              <li>· Facture entreprise</li>
              <li>· Livraison ou remise en main propre sur Nice</li>
            </ul>
          </Reveal>
          <Reveal className="lg:col-span-7" delay={0.1}>
            <form onSubmit={submit} className="bg-cream border border-line p-6 sm:p-9 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="b2b-form">
              <div>
                <Label htmlFor="b2b-company">Société *</Label>
                <Input id="b2b-company" data-testid="b2b-company" required value={form.company} onChange={set("company")} className="mt-1.5 bg-white" />
              </div>
              <div>
                <Label htmlFor="b2b-name">Votre nom *</Label>
                <Input id="b2b-name" data-testid="b2b-name" required value={form.contact_name} onChange={set("contact_name")} className="mt-1.5 bg-white" />
              </div>
              <div>
                <Label htmlFor="b2b-email">Email professionnel *</Label>
                <Input id="b2b-email" type="email" data-testid="b2b-email" required value={form.email} onChange={set("email")} className="mt-1.5 bg-white" />
              </div>
              <div>
                <Label htmlFor="b2b-phone">Téléphone</Label>
                <Input id="b2b-phone" data-testid="b2b-phone" value={form.phone} onChange={set("phone")} className="mt-1.5 bg-white" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="b2b-quantity">Quantité estimée</Label>
                <Input id="b2b-quantity" type="number" min="1" data-testid="b2b-quantity" value={form.quantity} onChange={set("quantity")} className="mt-1.5 bg-white" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="b2b-message">Votre besoin</Label>
                <Textarea id="b2b-message" rows={4} data-testid="b2b-message" value={form.message} onChange={set("message")} className="mt-1.5 bg-white"
                  placeholder="Cadeaux de fin d'année pour nos 80 collaborateurs…" />
              </div>
              <div className="sm:col-span-2">
                <button type="submit" disabled={sending} data-testid="b2b-submit"
                  className="inline-flex items-center gap-2 bg-rouge text-white px-8 py-4 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover transition-colors disabled:opacity-50">
                  <Send className="w-4 h-4" /> {sending ? "Envoi…" : "Envoyer ma demande"}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
